import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { trackEvent, trackReciprocity } from "../../../lib/track";

const initialForm = {
  name: "",
  email: "",
  role: "",
  testimonial: "",
  shift: "",
  advice: "",
  permission: false,
  ai_consent: false,
  rating: 5,
};

const STRIPPABLE_FIELDS = ["email", "role", "shift", "advice"];

const cleanPayload = (form) => {
  const payload = { ...form };
  STRIPPABLE_FIELDS.forEach((k) => {
    if (!payload[k] || !String(payload[k]).trim()) delete payload[k];
  });
  return payload;
};

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="text-[10px] tracking-[0.3em] uppercase text-themed-muted"
    >
      {label}
    </label>
    {children}
  </div>
);

const SubmittedState = ({ onShareAnother }) => {
  useEffect(() => {
    trackEvent("testimonial_gratitude_returned");
    trackReciprocity("testimonial_gratitude_note");
  }, []);
  return (
    <div className="text-center py-6" data-testid="testimonial-submitted">
      <h4 className="font-serif text-3xl text-themed">Thank you for adding your voice.</h4>
      <p className="mt-4 text-themed-muted max-w-md mx-auto leading-relaxed">
        Your reflection matters. Every honest testimony becomes part of the
        field that helps someone else recognize what is possible for them.
      </p>
      <p className="mt-3 font-serif italic text-themed max-w-md mx-auto">
        Thank you for returning your experience to the movement.
      </p>
      <p className="mt-4 text-xs text-themed-muted max-w-md mx-auto italic">
        Your submission may be reviewed before it appears publicly.
      </p>
      <button
        type="button"
        onClick={onShareAnother}
        className="btn-ghost-gold mt-8"
        data-testid="testimonial-share-another"
      >
        Share Another
      </button>
    </div>
  );
};

const PROMPT_LABELS = {
  book: "Reader of Authentic Momentum",
  blueprint: "Momentum Personal Action Plan client",
  build: "Website build client",
  consultation: "Consultation client",
};

const TestimonialForm = ({ promptContext = null }) => {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill the `role` field when the user picks a prompt chip in the
  // parent section. We only overwrite when role is empty so we don't
  // clobber whatever the user has already typed.
  useEffect(() => {
    if (!promptContext) return;
    const suggested = PROMPT_LABELS[promptContext.id];
    if (!suggested) return;
    setForm((f) => (f.role ? f : { ...f, role: suggested }));
  }, [promptContext]);

  const update = useCallback(
    (k) => (e) =>
      setForm((f) => ({
        ...f,
        [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
      })),
    []
  );

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!form.name || !form.testimonial) {
        toast.error("Please share at least your name and reflection.");
        return;
      }
      try {
        setBusy(true);
        await api.post("/testimonials", cleanPayload(form));
        trackEvent("testimonial_submitted");
        setSubmitted(true);
        setForm(initialForm);
        toast.success("Thank you for adding your wisdom.");
      } catch {
        toast.error("Submission paused. Please try again.");
      } finally {
        setBusy(false);
      }
    },
    [form]
  );

  if (submitted) {
    return (
      <form
        data-testid="testimonial-form"
        className="glass-card p-10 space-y-7"
        onSubmit={(e) => e.preventDefault()}
      >
        <SubmittedState onShareAnother={() => setSubmitted(false)} />
      </form>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="testimonial-form"
      className="glass-card p-10 space-y-7"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <Field label="Name">
          <input
            data-testid="testimonial-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            className="input-bare"
            required
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="testimonial-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            className="input-bare"
          />
        </Field>
      </div>
      <Field label="Role or project type">
        <input
          data-testid="testimonial-role"
          type="text"
          value={form.role}
          onChange={update("role")}
          className="input-bare"
          placeholder="e.g., Founder, Wellness Brand"
        />
      </Field>
      <Field label="Your reflection">
        <textarea
          data-testid="testimonial-text"
          value={form.testimonial}
          onChange={update("testimonial")}
          rows={4}
          className="input-bare resize-none"
          required
        />
      </Field>
      <Field label="What shifted for you?">
        <textarea
          data-testid="testimonial-shift"
          value={form.shift}
          onChange={update("shift")}
          rows={2}
          className="input-bare resize-none"
        />
      </Field>
      <Field label="What would you tell someone considering Authentic Momentum?">
        <textarea
          data-testid="testimonial-advice"
          value={form.advice}
          onChange={update("advice")}
          rows={2}
          className="input-bare resize-none"
        />
      </Field>
      <div className="flex items-center gap-3 pt-2">
        <input
          data-testid="testimonial-permission"
          type="checkbox"
          id="perm"
          checked={form.permission}
          onChange={update("permission")}
          className="accent-[#D4AF37]"
        />
        <label htmlFor="perm" className="text-xs text-themed-muted">
          I give permission to publish my reflection after review.
        </label>
      </div>
      <div className="flex items-start gap-3 pt-1">
        <input
          data-testid="testimonial-ai-consent"
          type="checkbox"
          id="ai-consent"
          checked={!!form.ai_consent}
          onChange={update("ai_consent")}
          className="accent-[#D4AF37] mt-[3px]"
        />
        <label htmlFor="ai-consent" className="text-xs text-themed-muted leading-relaxed">
          I consent to my reflection being lightly edited for clarity by Authentic Momentum, including the optional use of AI tools, while preserving my voice and meaning. Final approval will be requested before any edited version is published.
        </label>
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="testimonial-submit"
        className="btn-gold disabled:opacity-60"
      >
        {busy ? "Sending..." : "Send My Reflection"}
      </button>
    </form>
  );
};

export default TestimonialForm;
